import { useState, useEffect, useRef } from 'react'

const styles = {
  container: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap',
  },
  column: {
    flex: 1,
    minWidth: '300px',
    maxWidth: '400px',
  },
  columnTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '10px',
    padding: '10px',
    borderRadius: '8px',
    color: '#fff',
    textAlign: 'center',
  },
  productList: {
    maxHeight: '400px',
    overflowY: 'auto',
    border: '2px solid #ddd',
    borderRadius: '8px',
    background: '#fff',
  },
  productItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 15px',
    borderBottom: '1px solid #eee',
    background: '#fff',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  productItemDragging: {
    background: '#e0ffe0',
    opacity: 0.5,
  },
  productItemDragOver: {
    borderTop: '3px solid #4ecdc4',
  },
  productInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  productName: {
    fontSize: '14px',
    fontWeight: '500',
  },
  productPrice: {
    fontSize: '14px',
    color: '#666',
  },
  dragHandle: {
    cursor: 'grab',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  actionButton: {
    padding: '5px 10px',
    border: 'none',
    borderRadius: '5px',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '14px',
  },
  emptyState: {
    textAlign: 'center',
    color: '#999',
    padding: '20px',
  },
}

// Helper to compute products from props
function computeProducts(allProducts, selectedIds, sortOrder) {
  if (!allProducts || allProducts.length === 0) return { included: [], excluded: [] }
  
  const productMap = new Map(allProducts.map(p => [p.id, p]))
  const order = sortOrder.length > 0 ? sortOrder : selectedIds
  
  const included = []
  const excluded = []
  
  order.forEach(id => {
    const product = productMap.get(id)
    if (product) included.push(product)
  })
  
  allProducts.forEach(product => {
    if (!order.includes(product.id)) {
      excluded.push(product)
    }
  })
  
  return { included, excluded }
}

function ProductSelector({ allProducts, selectedIds = [], sortOrder = [], onChange }) {
  // Use refs to track latest props for the effect
  const propsRef = useRef({ allProducts, selectedIds, sortOrder })
  propsRef.current = { allProducts, selectedIds, sortOrder }
  
  // Initialize with empty - will be set by effect
  const [includedProducts, setIncludedProducts] = useState([])
  const [excludedProducts, setExcludedProducts] = useState([])
  const [isInitialized, setIsInitialized] = useState(false)
  const [draggedItem, setDraggedItem] = useState(null)
  const [dragOverIndex, setDragOverIndex] = useState(null)
  // Use ref to track current state for callbacks
  const includedRef = useRef([])
  const excludedRef = useRef([])

  // Initialize from props - runs whenever props change
  useEffect(() => {
    const { allProducts, selectedIds, sortOrder } = propsRef.current
    
    if (!allProducts || allProducts.length === 0) {
      return
    }
    
    const { included, excluded } = computeProducts(allProducts, selectedIds, sortOrder)
    
    // Only update if not yet initialized or if lists are actually different
    const currentIncludedIds = includedRef.current.map(p => p.id).sort().join(',')
    const newIncludedIds = included.map(p => p.id).sort().join(',')
    
    if (!isInitialized || currentIncludedIds !== newIncludedIds) {
      setIncludedProducts(included)
      includedRef.current = included
    }
    
    const currentExcludedIds = excludedRef.current.map(p => p.id).sort().join(',')
    const newExcludedIds = excluded.map(p => p.id).sort().join(',')
    
    if (!isInitialized || currentExcludedIds !== newExcludedIds) {
      setExcludedProducts(excluded)
      excludedRef.current = excluded
    }
    
    setIsInitialized(true)
  }, [allProducts, selectedIds, sortOrder])

  // Notify parent of changes
  const notifyChange = () => {
    if (onChange) {
      const ids = includedProducts.map(p => p.id)
      onChange(ids, ids)
    }
  }

  const moveToIncluded = (product) => {
    setExcludedProducts(prev => prev.filter(p => p.id !== product.id))
    setIncludedProducts(prev => {
      const newList = [...prev, product]
      // Notify after state update
      setTimeout(() => {
        if (onChange) {
          const ids = newList.map(p => p.id)
          onChange(ids, ids)
        }
      }, 0)
      return newList
    })
  }

  const moveToExcluded = (product) => {
    setIncludedProducts(prev => {
      const newList = prev.filter(p => p.id !== product.id)
      // Notify after state update
      setTimeout(() => {
        if (onChange) {
          const ids = newList.map(p => p.id)
          onChange(ids, ids)
        }
      }, 0)
      return newList
    })
    setExcludedProducts(prev => [...prev, product])
  }

  const handleDragStart = (e, product, index) => {
    setDraggedItem({ product, index })
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
    setDragOverIndex(index)
  }

  const handleDrop = (e, dropIndex) => {
    e.preventDefault()
    if (!draggedItem) return
    
    const newList = [...includedProducts]
    const [removed] = newList.splice(draggedItem.index, 1)
    newList.splice(dropIndex, 0, removed)
    
    setIncludedProducts(newList)
    // Notify after state update
    setTimeout(() => {
      if (onChange) {
        const ids = newList.map(p => p.id)
        onChange(ids, ids)
      }
    }, 0)
    setDraggedItem(null)
    setDragOverIndex(null)
  }

  return (
    <div style={styles.container}>
      {/* Shown Products Column */}
      <div style={styles.column}>
        <div style={{...styles.columnTitle, background: '#4ecdc4'}}>
          Produkte angezeigt ({includedProducts.length})
        </div>
        <div style={styles.productList}>
          {!isInitialized && (
            <div style={styles.emptyState}>
              Lädt...
            </div>
          )}
          {isInitialized && includedProducts.map((product, index) => (
            <div
              key={product.id}
              draggable
              onDragStart={(e) => handleDragStart(e, product, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}
              style={{
                ...styles.productItem,
                ...(draggedItem?.index === index ? styles.productItemDragging : {}),
              }}
            >
              <div style={styles.productInfo}>
                <span style={styles.dragHandle}>☰</span>
                <span style={styles.productName}>{product.name}</span>
                <span style={styles.productPrice}>{product.preis.toFixed(2)}€</span>
              </div>
              <button 
                style={{...styles.actionButton, background: '#ff6b6b'}} 
                onClick={() => moveToExcluded(product)}
              >
                ✕
              </button>
            </div>
          ))}
          {isInitialized && includedProducts.length === 0 && (
            <div style={styles.emptyState}>
              Keine Produkte ausgewählt
            </div>
          )}
        </div>
      </div>

      {/* Hidden Products Column */}
      <div style={styles.column}>
        <div style={{...styles.columnTitle, background: '#888'}}>
          Produkte ausgeblendet ({excludedProducts.length})
        </div>
        <div style={styles.productList}>
          {isInitialized && excludedProducts.map((product) => (
            <div 
              key={product.id} 
              style={styles.productItem}
              onClick={() => moveToIncluded(product)}
            >
              <div style={styles.productInfo}>
                <span style={styles.productName}>{product.name}</span>
                <span style={styles.productPrice}>{product.preis.toFixed(2)}€</span>
              </div>
              <button 
                style={{...styles.actionButton, background: '#4CAF50'}} 
                onClick={(e) => { e.stopPropagation(); moveToIncluded(product); }}
              >
                +
              </button>
            </div>
          ))}
          {isInitialized && excludedProducts.length === 0 && (
            <div style={styles.emptyState}>
              Keine weiteren Produkte
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProductSelector